import { useEffect, useState } from 'react';
import { Navigate, Route, Routes, useLocation, useMatch, useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { CURRENT_VERSION, isNewer, useAlerts, useApi, useDevices, useEvents, useMe, useMembers, useSaveSettings, useStatus } from '@glyph/core';
import { HeaderItem, Shell, peekScroll, useClock, useScrollMemory, useTheme } from '@glyph/ui';
import { AlertsScreen, CaptionsScreen, DevicesScreen, LiveScreen, MenuScreen, NetworkScreen, OutputsScreen, OverviewScreen, RecordingsScreen, RoomOperatorScreen, SpeechScreen, SystemScreen, UpdatesScreen, deviceStateOf } from '@glyph/screens';
import { SignInPage } from './pages/SignInPage';
import { InvitePage } from './pages/InvitePage';
import { AdminPage, type AdminTab } from './pages/AdminPage';
import { CONSOLE_BUILD } from './update-check';

// Same order and names as the unit's own shell, plus Overview (cloud-only: latency, history, alerts).
const DEVICE_PAGES = [
  { key: 'overview', label: 'Overview', title: 'Overview' },
  { key: 'live', label: 'Live', title: 'Live' },
  { key: 'captions', label: 'Captions', title: 'Captions' },
  { key: 'speech', label: 'Speech', title: 'Speech' },
  { key: 'recordings', label: 'Recordings', title: 'Recordings' },
  { key: 'outputs', label: 'Outputs', title: 'Outputs' },
  { key: 'network', label: 'Network', title: 'Network' },
  { key: 'system', label: 'System', title: 'System' },
] as const;
// Device pages an account would only be refused on are not shown (the cloud enforces the rules either way): a
// viewer watches, it changes nothing; network changes are admin work (event admins included).
const HIDDEN_DEVICE_PAGES: Record<string, readonly string[]> = {
  viewer: ['captions', 'speech', 'outputs', 'network', 'system'],
  operator: ['network'],
};
const FLEET_PAGES = [
  { key: 'fleet', path: '/', label: 'Devices', title: 'Devices' },
  { key: 'alerts', path: '/alerts', label: 'Alerts', title: 'Alerts' },
  { key: 'updates', path: '/updates', label: 'Updates', title: 'Updates' },
] as const;
// Which admin pages each role sees (the cloud enforces the same): owner / admin / platform all of them; an event
// admin its team and the presets; an operator the presets (to apply them); viewers and room operators none.
const ADMIN_PAGES = [
  { key: 'users', path: '/admin/users', label: 'Users', title: 'Users & access' },
  { key: 'presets', path: '/admin/presets', label: 'Presets', title: 'Presets' },
  { key: 'logins', path: '/admin/logins', label: 'Logins', title: 'Logins' },
  { key: 'groups', path: '/admin/groups', label: 'Groups', title: 'Groups' },
  { key: 'schedules', path: '/admin/schedules', label: 'Schedules', title: 'Caption schedules' },
] as const;
const ADMIN_PAGES_FOR: Record<string, readonly string[]> = {
  platform: ['users', 'presets', 'logins', 'groups', 'schedules'],
  owner: ['users', 'presets', 'logins', 'groups', 'schedules'],
  admin: ['users', 'presets', 'logins', 'groups', 'schedules'],
  event_admin: ['users', 'presets'],
  operator: ['presets'],
};
const LAST_DEVICE = 'glyph-last-device';

export function App() {
  const me = useMe();
  const loc = useLocation();
  const nav = useNavigate();
  useTheme(); // applies the saved theme before sign-in too, not only inside the console shell
  if (me.isLoading) return null;
  const inviteToken = loc.pathname === '/invite' ? new URLSearchParams(loc.search).get('token') : null;
  if (inviteToken)
    return (
      <InvitePage
        token={inviteToken}
        onDone={() => {
          nav('/', { replace: true });
          void me.refetch();
        }}
      />
    );
  if (me.isError || !me.data) return <SignInPage onSignedIn={() => void me.refetch()} />;
  // A room operator gets one page: its rooms and where their captions sit.
  if (me.data.role === 'room_operator') return <RoomConsole />;
  return <Routes>{routes(loc.pathname)}</Routes>;
}

function RoomConsole() {
  const api = useApi();
  const qc = useQueryClient();
  return (
    <RoomOperatorScreen
      onSignOut={() =>
        void api.logout().then(() => {
          qc.clear();
          window.location.reload();
        })
      }
    />
  );
}

function routes(_path: string) {
  return (
    <>
      <Route path="/login" element={<Navigate to="/" replace />} />
      <Route path="/*" element={<Console />} />
    </>
  );
}
function ToLive() {
  const { id } = useParams();
  return <Navigate to={`/devices/${id}/overview`} replace />;
}

function Console() {
  const nav = useNavigate();
  const loc = useLocation();
  const pageMatch = useMatch('/devices/:id/:page');
  const idMatch = useMatch('/devices/:id');
  const params: { id?: string; page?: string } = pageMatch?.params ?? idMatch?.params ?? {};
  const api = useApi();
  const qc = useQueryClient();
  const me = useMe();
  const devices = useDevices();
  const alerts = useAlerts(true);
  const hiddenPages = HIDDEN_DEVICE_PAGES[me.data?.role ?? ''] ?? [];
  const devicePages = DEVICE_PAGES.filter((p) => !hiddenPages.includes(p.key));
  const adminPages = ADMIN_PAGES.filter((p) => (ADMIN_PAGES_FOR[me.data?.role ?? ''] ?? []).includes(p.key));
  const isAdmin = adminPages.length > 0;
  const canUsers = adminPages.some((p) => p.key === 'users');
  const isPlatform = me.data?.role === 'platform'; // rollouts are platform work; everyone else never sees the Updates page
  const fleetPages = FLEET_PAGES.filter((p) => p.key !== 'updates' || isPlatform);
  const members = useMembers(canUsers);
  const shows = useEvents();
  const showMatch = useMatch('/groups/:eventId');
  const oldShowMatch = useMatch('/shows/:eventId');
  const adminMatch = useMatch('/admin/:tab');
  const { theme, toggle, setTheme } = useTheme();
  const clock = useClock();
  // Each page keeps its own scroll position; a page not yet visited opens at the top.
  useScrollMemory(loc.pathname);
  const [lastDevice, setLastDevice] = useState<string>(() => localStorage.getItem(LAST_DEVICE) ?? '');
  // Pending units are listed on the Devices page for approval only: never the selected device.
  const devs = (devices.data ?? []).filter((d) => d.status !== 'pending');
  const deviceId = params.id ?? lastDevice ?? devs[0]?.id ?? '';
  useEffect(() => {
    if (params.id) {
      setLastDevice(params.id);
      localStorage.setItem(LAST_DEVICE, params.id);
    }
  }, [params.id]);
  // Only an id the fleet list knows is a device: a stale localStorage entry or a hand-typed URL
  // segment must not become an API path (`/devices/outputs/cmd`).
  const fallbackId = devs.some((d) => d.id === deviceId) ? deviceId : (devs[0]?.id ?? '');
  const fleetLoaded = devices.isSuccess;
  // No device yet (fresh fleet): the hooks stay idle on an empty id instead of polling a placeholder.
  const status = useStatus(fallbackId);
  const s = useSaveSettings(fallbackId);
  const dev = devs.find((d) => d.id === fallbackId);
  const devPage = params.page ?? null;
  const fleetPage = FLEET_PAGES.find((p) => p.path === loc.pathname)?.key ?? (loc.pathname === '/menu' ? 'more' : null);
  const adminTab = (adminMatch?.params.tab ?? null) as AdminTab | null;
  const now = Date.now() / 1000;
  // Groups in the sidebar: active now or still to come, soonest first (ended ones stay in Admin / Groups).
  const liveShows = [...(shows.data ?? [])].filter((e) => e.ends_at >= now).sort((a, b) => a.starts_at - b.starts_at);
  const show = showMatch ? (shows.data ?? []).find((e) => e.id === showMatch.params.eventId) : undefined;
  const isMore = loc.pathname === '/menu';
  const st = status.data;
  const state = dev ? (dev.online ? deviceStateOf(st, true) : 'offline') : 'offline';
  const openCount = alerts.data?.length ?? 0;
  const stable = CURRENT_VERSION;
  const behind = devs.filter((d) => isNewer(stable, d.agent_version)).length;
  const goDev = (page: string, id = fallbackId) => () => {
    if (id) nav(`/devices/${id}/${page}`);
  };
  const hasDevice = !!fallbackId || !fleetLoaded; // keep the device nav while the first fleet list is still loading
  // A device page for an id the fleet does not know goes home once the list has loaded.
  const devRoute = (el: JSX.Element) =>
    fleetLoaded && !devs.some((d) => d.id === params.id) ? <Navigate to="/" replace /> : devPage && hiddenPages.includes(devPage) ? <Navigate to={`/devices/${params.id}/overview`} replace /> : el;
  const devMeta: Record<string, string | undefined> = {
    overview: dev ? (dev.online ? 'online' : 'offline') : '',
    system: st?.version?.agent ?? '',
    live: st?.engine?.running ? 'ON AIR' : '',
    captions: s.isDirty('display') ? 'unsaved' : '',
    speech: s.saved?.deepgram.model,
    recordings: undefined,
    outputs: s.saved ? (s.saved.outputs.policy === 'mirror' ? 'A = B' : 'A ≠ B') : '',
    network: st?.network?.ap.enabled ? 'hotspot' : '',
  };
  const fleetMeta: Record<string, string> = { fleet: String(devs.length), alerts: openCount ? `${openCount} open` : '', updates: behind ? `${behind} behind` : '' };
  const adminMeta: Record<string, string> = { users: String(members.data?.length ?? ''), presets: '', logins: '', groups: String(shows.data?.length ?? ''), schedules: '' };
  const signOut = async () => {
    await api.logout();
    qc.clear();
    nav('/');
    window.location.reload();
  };
  const titleOf = () =>
    devPage
      ? DEVICE_PAGES.find((p) => p.key === devPage)?.title ?? 'Live'
      : adminTab
        ? ADMIN_PAGES.find((p) => p.key === adminTab)?.title ?? 'Admin'
        : show
          ? show.name
          : FLEET_PAGES.find((p) => p.key === fleetPage)?.title ?? (isMore ? 'Menu' : 'Devices');
  const crumb = devPage ? `Device / ${dev?.name ?? '…'} / ${titleOf()}` : adminTab ? `Admin / ${titleOf()}` : show ? `Group / ${show.name}` : `Fleet / ${titleOf()}`;
  // Phone: the bottom bar is about where you are. On a unit's pages it is that unit's tabs (and a way back to the
  // list); everywhere else it is the fleet. The sidebar keeps the full tree on desktop.
  const onDevice = !!devPage;
  const moreKeys = ['recordings', 'outputs', 'network', 'system'];
  const moreActive = isMore || (devPage ? moreKeys.includes(devPage) : !!adminTab || !!show);
  const menuEl = (forDevice: boolean) => (
    <MenuScreen
      devices={devs}
      currentDeviceId={fallbackId}
      onPickDevice={(id) => nav(`/devices/${id}/overview`)}
      deviceTitle={dev?.name ?? 'Device'}
      deviceEntries={hasDevice && forDevice ? devicePages.filter((p) => moreKeys.includes(p.key)).map((p) => ({ key: p.key, label: p.label, meta: devMeta[p.key], onSelect: goDev(p.key) })) : []}
      fleetEntries={[
        ...fleetPages.filter((p) => p.key !== 'fleet').map((p) => ({ key: p.key, label: p.label, meta: fleetMeta[p.key], onSelect: () => nav(p.path) })),
        ...liveShows.map((e) => ({ key: `group-${e.id}`, label: e.name, meta: 'group', onSelect: () => nav(`/groups/${e.id}`) })),
        ...adminPages.map((p) => ({ key: p.key, label: p.label, meta: adminMeta[p.key], onSelect: () => nav(p.path) })),
      ]}
      theme={theme}
      onTheme={setTheme}
      user={me.data ? { name: me.data.name, role: me.data.role } : undefined}
      relayOk={devs.some((d) => d.online)}
      onSignOut={() => void signOut()}
      build={CONSOLE_BUILD}
    />
  );
  const fleetEl = <DevicesScreen onOpenDevice={(id) => nav(`/devices/${id}/overview`)} onGoAlerts={() => nav('/alerts')} onGoUpdates={(ids) => nav('/updates', { state: ids?.length ? { select: ids } : undefined })} onOpenGroup={(id) => nav(`/groups/${id}`)} />;
  const mobileTabs = onDevice
    ? [
        { key: 'fleet', label: 'Devices', active: false, onSelect: () => nav('/'), preview: fleetEl, scrollY: peekScroll('/') },
        { key: 'overview', label: 'Overview', active: devPage === 'overview', onSelect: goDev('overview'), preview: <OverviewScreen deviceId={fallbackId} />, scrollY: peekScroll(`/devices/${fallbackId}/overview`) },
        { key: 'live', label: 'Live', active: devPage === 'live', onSelect: goDev('live'), preview: <LiveScreen deviceId={fallbackId} />, scrollY: peekScroll(`/devices/${fallbackId}/live`) },
        ...(hiddenPages.includes('captions') ? [] : [{ key: 'captions', label: 'Captions', active: devPage === 'captions', onSelect: goDev('captions'), preview: <CaptionsScreen deviceId={fallbackId} />, scrollY: peekScroll(`/devices/${fallbackId}/captions`) }]),
        { key: 'more', label: 'More', active: moreActive, onSelect: () => nav('/menu', { state: { device: true } }), badge: openCount > 0, preview: menuEl(true), scrollY: peekScroll('/menu') },
      ]
    : [
        { key: 'fleet', label: 'Devices', active: fleetPage === 'fleet', onSelect: () => nav('/'), preview: fleetEl, scrollY: peekScroll('/') },
        { key: 'alerts', label: 'Alerts', active: fleetPage === 'alerts', onSelect: () => nav('/alerts'), badge: openCount > 0, preview: <AlertsScreen />, scrollY: peekScroll('/alerts') },
        ...(isPlatform ? [{ key: 'updates', label: 'Updates', active: fleetPage === 'updates', onSelect: () => nav('/updates'), preview: <UpdatesScreen fleetStable={stable} />, scrollY: peekScroll('/updates') }] : []),
        { key: 'more', label: 'More', active: moreActive, onSelect: () => nav('/menu'), preview: menuEl(false), scrollY: peekScroll('/menu') },
      ];
  const menuForDevice = isMore && !!(loc.state as { device?: boolean } | null)?.device;

  return (
    <Shell
      logoSub={`${me.data?.org.name ?? ''} · Cloud`}
      groups={[
        { label: 'Fleet', items: fleetPages.map((p) => ({ key: p.key, label: p.label, meta: fleetMeta[p.key], active: fleetPage === p.key, onSelect: () => nav(p.path) })) },
        ...(liveShows.length
          ? [{ label: 'Groups', items: liveShows.map((e) => ({ key: e.id, label: e.name, meta: e.starts_at > now ? 'soon' : `${devs.filter((d) => d.assignment?.event_id === e.id).length} units`, active: show?.id === e.id, onSelect: () => nav(`/groups/${e.id}`) })) }]
          : []),
        ...(isAdmin
          ? [{ label: 'Admin', items: adminPages.map((p) => ({ key: p.key, label: p.label, meta: adminMeta[p.key], active: adminTab === p.key, onSelect: () => nav(p.path) })) }]
          : []),
        // Device pages only once the fleet has a device to show them for.
        ...(hasDevice
          ? [
              {
                label: 'Device',
                deviceCard: dev ? { name: dev.name, event: dev.assignment?.event_name ?? 'Unassigned', state } : undefined,
                items: devicePages.map((p) => ({ key: p.key, label: p.label, meta: devMeta[p.key], active: devPage === p.key, onSelect: goDev(p.key) })),
              },
            ]
          : []),
      ]}
      user={me.data ? { name: me.data.name, role: me.data.role } : undefined}
      onSignOut={() => void signOut()}
      crumb={crumb}
      headerItems={
        <>
          <HeaderItem>
            RELAY <span className={devs.some((d) => d.online) ? 'text-ok' : 'text-warn'}>{devs.some((d) => d.online) ? 'OK' : 'DOWN'}</span> · {location.host}
          </HeaderItem>
          <HeaderItem tabular>{clock} UTC</HeaderItem>
        </>
      }
      theme={theme}
      onToggleTheme={toggle}
      mobileTitle={titleOf()}
      mobileDevice={onDevice && dev ? { name: dev.name, state, onClick: () => nav('/menu', { state: { device: true } }) } : undefined}
      tabs={mobileTabs}
    >
      <Routes>
        <Route path="/" element={fleetEl} />
        <Route path="/alerts" element={<AlertsScreen />} />
        <Route path="/updates" element={isPlatform ? <UpdatesScreen fleetStable={stable} initialSelection={(loc.state as { select?: string[] } | null)?.select} /> : <Navigate to="/" replace />} />
        <Route path="/shows/:eventId" element={<Navigate to={`/groups/${oldShowMatch?.params.eventId ?? ''}`} replace />} />
        <Route path="/groups/:eventId" element={show ? <DevicesScreen eventId={show.id} title={show.name} onOpenDevice={(id) => nav(`/devices/${id}/overview`)} onGoAlerts={() => nav('/alerts')} onGoUpdates={(ids) => nav('/updates', { state: ids?.length ? { select: ids } : undefined })} /> : shows.isSuccess ? <Navigate to="/" replace /> : null} />
        {isAdmin && <Route path="/admin/:tab" element={adminTab && adminPages.some((p) => p.key === adminTab) ? <AdminPage tab={adminTab} onTab={(t) => nav(`/admin/${t}`)} onOpenGroup={(id) => nav(`/groups/${id}`)} /> : <Navigate to={adminPages[0]!.path} replace />} />}
        <Route path="/menu" element={menuEl(menuForDevice)} />
        <Route path="/devices/:id" element={devRoute(<ToLive />)} />
        <Route path="/devices/:id/live" element={devRoute(<LiveScreen deviceId={fallbackId} />)} />
        <Route path="/devices/:id/captions" element={devRoute(<CaptionsScreen deviceId={fallbackId} />)} />
        <Route path="/devices/:id/speech" element={devRoute(<SpeechScreen deviceId={fallbackId} />)} />
        <Route path="/devices/:id/recordings" element={devRoute(<RecordingsScreen deviceId={fallbackId} />)} />
        <Route path="/devices/:id/outputs" element={devRoute(<OutputsScreen deviceId={fallbackId} />)} />
        <Route path="/devices/:id/network" element={devRoute(<NetworkScreen deviceId={fallbackId} />)} />
        <Route path="/devices/:id/overview" element={devRoute(<OverviewScreen deviceId={fallbackId} />)} />
        <Route path="/devices/:id/system" element={devRoute(<SystemScreen deviceId={fallbackId} />)} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Shell>
  );
}
